import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, MessageCircle, AlertTriangle } from "lucide-react";
import api, { track } from "@/lib/api";
import { formatPrice, formatDate } from "@/lib/format";
import { useSettings } from "@/context/SettingsContext";

const BENEFITS = ["Todos os cursos", "Videoaulas", "Tabuleiro interativo", "Puzzles", "Exercícios", "Sistema de níveis", "Progresso salvo", "Novas aulas", "Acesso pelo celular"];

const STATUS_LABEL = {
  ACTIVE: { t: "Ativa", c: "text-chessup-turq" }, TRIALING: { t: "Período de teste", c: "text-chessup-turq" },
  PAST_DUE: { t: "Pagamento pendente", c: "text-red-300" }, CANCELED: { t: "Cancelada", c: "text-chessup-subtext" },
  EXPIRED: { t: "Expirada", c: "text-chessup-subtext" }, ADMIN_GRANTED: { t: "Acesso concedido", c: "text-chessup-goldlight" },
  FREE: { t: "Gratuita", c: "text-chessup-subtext" },
};

export default function Subscription() {
  const { openWhatsapp } = useSettings();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/me/subscription").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const subscribe = async () => {
    setLoading(true);
    track("checkout_started", "pricing");
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error("Não foi possível iniciar o pagamento."); setLoading(false); }
  };

  const portal = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) { toast.error("Portal indisponível no momento."); setLoading(false); }
  };

  if (!data) return <div className="p-8"><div className="h-8 w-48 bg-chessup-card rounded animate-pulse" /></div>;

  const sub = data.subscription;
  const isPremium = data.is_premium;
  const st = STATUS_LABEL[sub?.status || "FREE"];

  return (
    <div className="p-5 sm:p-8 max-w-3xl mx-auto">
      <h1 className="font-heading text-3xl text-chessup-text mb-6">Minha Assinatura</h1>

      {sub?.status === "PAST_DUE" && (
        <div className="mb-6 rounded-xl border border-red-500/40 bg-red-500/10 p-5" data-testid="past-due-banner">
          <div className="flex items-center gap-2 text-red-200 font-medium"><AlertTriangle className="h-5 w-5" /> Não conseguimos renovar sua assinatura.</div>
          <p className="text-sm text-chessup-subtext mt-2">Atualize sua forma de pagamento para continuar estudando sem interrupções.</p>
          <button onClick={portal} className="mt-4 rounded-full bg-chessup-gold px-5 py-2.5 text-sm font-semibold text-chessup-deep">ATUALIZAR PAGAMENTO</button>
        </div>
      )}

      {sub?.cancel_at_period_end && sub?.status !== "CANCELED" && (
        <div className="mb-6 rounded-xl border border-chessup-gold/40 bg-chessup-gold/8 p-4 text-sm text-chessup-text">
          Sua assinatura será encerrada em {formatDate(sub.current_period_end)}. Seu progresso ficará salvo.
        </div>
      )}

      {isPremium ? (
        <div className="rounded-2xl border border-chessup-border bg-chessup-card p-6" data-testid="billing-panel">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-chessup-gold/15 border border-chessup-gold/40 px-3 py-1 text-xs font-semibold text-chessup-goldlight mb-2">PREMIUM</div>
              <h2 className="font-heading text-2xl text-chessup-text">{data.plan_name}</h2>
              <div className={`text-sm mt-1 ${st.c}`}>Status: {st.t}</div>
            </div>
            <div className="text-right"><div className="text-2xl font-bold text-chessup-text">{formatPrice(data.price_amount, data.price_currency)}</div><div className="text-sm text-chessup-subtext">/mês</div></div>
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-y-2 text-sm">
            <div className="text-chessup-subtext">Início da assinatura</div><div className="text-chessup-text sm:text-right">{formatDate(sub?.current_period_start)}</div>
            <div className="text-chessup-subtext">Próxima renovação</div><div className="text-chessup-text sm:text-right">{sub?.cancel_at_period_end ? "—" : formatDate(sub?.current_period_end)}</div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            {sub?.stripe_customer_id && <button onClick={portal} disabled={loading} data-testid="manage-subscription" className="rounded-full bg-chessup-gold px-5 py-2.5 text-sm font-semibold text-chessup-deep hover:bg-chessup-goldlight">GERENCIAR ASSINATURA</button>}
            <button onClick={() => openWhatsapp("billing")} className="inline-flex items-center gap-2 rounded-full border border-chessup-border px-5 py-2.5 text-sm text-chessup-text hover:border-chessup-turq"><MessageCircle className="h-4 w-4" /> Suporte de cobrança</button>
          </div>
          {sub?.status === "ADMIN_GRANTED" && <p className="mt-4 text-xs text-chessup-goldlight">Acesso concedido pela equipe ChessUp até {formatDate(sub.current_period_end)}.</p>}
        </div>
      ) : (
        <div className="rounded-2xl border border-chessup-gold/40 bg-chessup-card p-8 shadow-[0_0_40px_rgba(212,175,55,0.12)]">
          {sub?.status === "CANCELED" && <p className="mb-4 text-chessup-goldlight">Que bom ter você de volta. Seu progresso está esperando por você.</p>}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-heading text-2xl text-chessup-text">{data.plan_name}</h2>
            <div className="text-right"><div className="text-3xl font-bold text-chessup-text">{formatPrice(data.price_amount, data.price_currency)}</div><div className="text-sm text-chessup-subtext">/mês</div></div>
          </div>
          <ul className="mt-6 grid sm:grid-cols-2 gap-2.5">
            {BENEFITS.map((b) => <li key={b} className="flex items-center gap-2 text-sm text-chessup-text"><Check className="h-4 w-4 text-chessup-turq shrink-0" /> {b}</li>)}
          </ul>
          <button onClick={subscribe} disabled={loading} data-testid="subscribe-btn" className="mt-8 w-full rounded-full bg-chessup-gold py-3.5 font-semibold text-chessup-deep hover:bg-chessup-goldlight disabled:opacity-60">
            {loading ? "Redirecionando..." : (sub?.status === "CANCELED" ? "REATIVAR ASSINATURA" : "ASSINAR CHESSUP ACADEMY")}
          </button>
          <button onClick={() => openWhatsapp("pricing")} className="mt-3 w-full rounded-full border border-chessup-border py-3 text-sm font-semibold text-chessup-text hover:border-chessup-turq">TIRAR DÚVIDA NO WHATSAPP</button>
        </div>
      )}
    </div>
  );
}
